import {
  Flex,
  Loader,
  Text,
  Title,
  Paper,
  Alert,
  Divider,
  Badge,
} from "@mantine/core";
import React, { useMemo, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import EmptyCard from "../EmptyCard/EmptyCard";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import {
  IconAlertCircle,
  IconUsers,
  IconFocus,
  IconAlarmAverage,
} from "@tabler/icons-react";
import { formatNumber } from "utils/common";

const MeVsMeetingCard = ({
  type = "organization",
  label,
  data,
  isLoading,
  isError,
  source = "myTime",
  duration,
  self,
  uid,
}) => {
  const { openDrilldown } = useInsightsDrilldown();
  const myTimeName = source === "myTime" ? "Me Time" : "Productive Time";

  const redisKey = useMemo(() => {
    if (!uid || !duration || !self || source !== "myOrg") {
      return null;
    }
    return `users:${uid}:orgCalculations:timeSplit:${duration}:${self}:totalMeetingDetails`;
  }, [uid, duration, self, source]);

  const handlePieClick = useCallback(
    (params) => {
      // Only handle clicks on "Meeting Time" (blue area) when source is "myOrg"
      if (source === "myOrg" && params.name === "Meeting Time") {
        openDrilldown({
          name: "Meeting Time Details",
          type: "Meetings",
          meetingType: "All",
          redisKey,
        });
      }
    },
    [source, openDrilldown, redisKey]
  );

  const onEvents = useMemo(() => {
    if (source === "myOrg") {
      return { click: handlePieClick };
    }
    return {};
  }, [handlePieClick, source]);

  const option = {
    tooltip: {
      trigger: "item",
      formatter: "{b}: {c} hrs ({d}%)",
    },
    legend: {
      bottom: "0",
      left: "center",
      itemWidth: 12,
      itemHeight: 12,
      textStyle: {
        fontSize: 12,
      },
    },
    series: [
      {
        name: "Time split",
        type: "pie",
        radius: ["40%", "70%"],
        avoidLabelOverlap: true,
        label: {
          show: false,
        },
        data: [
          {
            value: data?.meHrs < 0 ? 0 : (data?.meHrs)?.toFixed(1),
            name: myTimeName,
          },
          {
            value: data?.meetingHrs < 0 ? 0 : (data?.meetingHrs)?.toFixed(1),
            name: "Meeting Time",
          },
        ],
        color: ["#fab005", "#39a8f0"],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      },
    ],
  };

  if (isLoading) {
    return (
      <MeVsMeetingCardWrapper data={data} label={label}>
        <div className="loader">
          <Loader size="sm" />
          <Text mt={8} size="sm" color="dimmed">
            Fetching time split
          </Text>
        </div>
      </MeVsMeetingCardWrapper>
    );
  }
  if (isError) {
    return (
      <MeVsMeetingCardWrapper data={data} label={label}>
        <div className="loader">
          <Text color="red" size="sm">
            Error while fetching time split
          </Text>
        </div>
      </MeVsMeetingCardWrapper>
    );
  }

  if (!isLoading && !isError && !data?.meHrs && !data?.meetingHrs) {
    return (
      <MeVsMeetingCardWrapper data={data} label={label}>
        <EmptyCard />
      </MeVsMeetingCardWrapper>
    );
  }

  // Calculate the meeting to me time ratio
  const calculateRatio = () => {
    if (!data?.meetingHrs || !data?.meHrs || data.meHrs <= 0)
      return "heavily weighted toward meetings";

    const ratio = (data.meetingHrs / Math.max(0.1, data.meHrs)).toFixed(1);

    if (ratio < 0.25) return "strongly focused on productive work";
    if (ratio < 0.5) return "balanced with more productive work";
    if (ratio < 1) return "balanced with slightly more productive work";
    if (ratio === 1) return "perfectly balanced";
    if (ratio < 2) return "balanced with slightly more meetings";
    if (ratio < 4) return "meeting-heavy";
    return "extremely meeting-intensive";
  };

  return (
    <MeVsMeetingCardWrapper data={data} label={label}>
      <ReactECharts
        option={option}
        style={{ height: "240px", width: "100%", alignSelf: "center" }}
        opts={{ renderer: "svg" }}
        onEvents={onEvents}
      />

      <Flex gap={10} mt={10} wrap="wrap">
        <Badge color="blue" leftSection={<IconUsers size={12} />}>
          Meeting: {formatNumber(data?.meetingHrs || 0)} hrs
        </Badge>
        <Badge color="green" leftSection={<IconFocus size={12} />}>
          {myTimeName}: {formatNumber(Math.max(0, data?.meHrs || 0))} hrs
        </Badge>
      </Flex>

      {data?.meHrs < 0 && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          mt={15}
          p="xs"
          radius="md"
          title="Time Overrun"
        >
          {type === "user" ? (
            <Text size="sm">
              You spent {Math.ceil(Math.abs(data.meHrs))} hours beyond your
              8-hour workday in meetings.
            </Text>
          ) : (
            <Text size="sm">
              Your team spent {Math.ceil(Math.abs(data.meHrs))} hours beyond
              their 8-hour workday in meetings.
            </Text>
          )}
        </Alert>
      )}

      {/* Show the summary message for user type when internal/external hours aren't available */}
      {type === "user" &&
        typeof data?.internalMeetingHours !== "number" &&
        typeof data?.externalMeetingHours !== "number" &&
        data?.totalWorkingHours && (
          <Flex direction="column" mt={15}>
            <Divider my={10} />
            <Alert
              icon={
                data?.meetingHrs > data?.meHrs ? (
                  <IconUsers size={16} />
                ) : (
                  <IconFocus size={16} />
                )
              }
              color={data?.meetingHrs > data?.meHrs ? "blue" : "green"}
              variant="light"
              size="sm"
              h={90}
            >
              In the past {data.pastDays || 7} days, you had{" "}
              {Math.ceil(data.totalWorkingHours)} working hours total. Your
              schedule is {calculateRatio()}.
            </Alert>
          </Flex>
        )}

      {type === "myOrg" &&
        // typeof data?.internalMeetingHours !== "number" &&
        // typeof data?.externalMeetingHours !== "number" &&
        data?.totalWorkingHours && (
          <Flex direction="column" mt={15}>
            <Divider my={10} />
            <Alert
              icon={
                data?.meetingHrs > data?.meHrs ? (
                  <IconUsers size={16} />
                ) : (
                  <IconFocus size={16} />
                )
              }
              color={data?.meetingHrs > data?.meHrs ? "blue" : "green"}
              variant="light"
              size="sm"
            >
              In the past <b>{data.pastDays}</b> days, your team had{" "}
              <b>{Math.ceil(data.totalWorkingHours)}</b> working hours total.
              Their schedule is {calculateRatio()}.
            </Alert>
          </Flex>
        )}
    </MeVsMeetingCardWrapper>
  );
};

export default MeVsMeetingCard;

const MeVsMeetingCardWrapper = ({ children, data, label = "Time split" }) => {
  return (
    <Paper p="md" radius="md" withBorder className="card">
      <Flex direction="column" sx={{ width: "100%", height: "100%" }}>
        <Flex align="center">
          <IconAlarmAverage size={18} style={{ marginRight: 8 }} />
          <Title order={5}>{label}</Title>
        </Flex>

        {/* checking if data available */}
        {children}

        {/* only for my organization, this data is available */}
        {/* {typeof data?.externalMeetingHours === "number" &&
          typeof data?.internalMeetingHours === "number" && (
            <>
              <Divider my={15} />
              <Flex direction="column" gap={5}>
                <Text size="sm">
                  Internal meetings: <b>{(data?.internalMeetingHours).toFixed(1)} hrs</b>
                </Text>
                <Text size="sm">
                  External meetings: <b>{(data?.externalMeetingHours).toFixed(1)} hrs</b>
                </Text>
              </Flex>
            </>
          )} */}
      </Flex>
    </Paper>
  );
};
